import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { DeviationProduct } from "@/types";
import {
  activateDeviationProduct,
  deactivateDeviationProduct,
  useGetDeviationProducts,
} from "@/services/DeviationProducts";
import Group from "@/components/atoms/Group";
import Text from "@/components/atoms/Text";
import Space from "@/components/atoms/Space";
import CTA from "@/components/atoms/CTA";
import { SettingsFormContent } from "@/components/pages/Settings";
import SettingsTable from "../../molecules/SettingsTable/SettingsTable";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

interface DeviationProductTableProps {
  isAdmin: boolean;
  companyId: number | undefined;
  setSelectedProduct: React.Dispatch<React.SetStateAction<DeviationProduct | null>>;
}

/** The company's deviation products; deactivated ones are hidden unless asked for. */
export default function DeviationProductTable({ isAdmin, companyId, setSelectedProduct }: DeviationProductTableProps) {
  const [showDisabled, setShowDisabled] = useState<boolean>(false);
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const { data: products } = useGetDeviationProducts(companyId as number, showDisabled, {
    enabled: !!companyId,
  });

  const { mutateAsync: mutateSetProductActive } = useMutation({
    mutationFn: async ({ productId, active }: { productId: number; active: boolean }) => {
      await (active ? activateDeviationProduct(productId) : deactivateDeviationProduct(productId));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["deviationProducts", companyId] });
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:generic.updateSuccess"))
      );
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:generic.updateError"))
      );
    },
  });

  const columns: ColumnDef<DeviationProduct>[] = useMemo(
    () => [
      {
        header: t("common:name"),
        accessorKey: "name",
      },
      {
        header: t("common:price"),
        accessorKey: "price",
        cell: ({ row }) => <span>{row.original.price + " " + row.original.currency}</span>,
      },
      {
        header: t("common:unit"),
        accessorKey: "priceUnit",
        cell: ({ row }) => <span>{t(`enumerables:deviationPaymentInvervalById.${row.original.priceUnit}`)}</span>,
      },
      {
        header: t("common:description"),
        accessorKey: "description",
      },
    ],
    [t]
  );

  // Deactivated products stay (old inspections refer to them) and can be activated again.
  const editActions = (product: DeviationProduct) =>
    product.disabled
      ? [
          {
            label: t("actions:activate"),
            action: async () => {
              await mutateSetProductActive({ productId: product.id, active: true });
            },
          },
        ]
      : [
          {
            label: t("actions:edit"),
            action: () => setSelectedProduct(product),
          },
          {
            label: t("common:deactivate"),
            action: async () => {
              await mutateSetProductActive({ productId: product.id, active: false });
            },
          },
        ];

  return (
    <SettingsFormContent>
      <Group justify="space-between">
        <Text size="md" strong={true} display="technical" tt="uppercase">
          {t("settings:deviationSettings.productList")}
        </Text>
        <CTA type="button" intent="secondary" onClick={() => setShowDisabled(!showDisabled)}>
          {showDisabled ? t("actions:hideDeactivated") : t("actions:showDeactivated")}
        </CTA>
      </Group>
      <Space height="25px" />
      <SettingsTable
        data={products || []}
        columns={columns}
        editActions={editActions}
        isAdmin={isAdmin}
        isRowInactive={(product) => product.disabled}
      />
    </SettingsFormContent>
  );
}
